import { FC, useState } from 'react';
import { DataRoomAccessPermission, DataRoomRecipientEligibility } from 'shared';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, InfoCircle, XCloseIcon } from '../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { Tag, TagVariant } from '../../../../../components/tag/index';
import { useLocale } from '../../../../../hooks/index';
import { useRecipientEligibility } from '../../../../../hooks/space/index';
import { useDebounce } from '../../../../../hooks/use-debounce.hook';
import { StringKey } from '../../../../../lang/index';
import { AccessRole } from '../../../../../types/stakeholder.types';
import { AccessAvatar } from './access-avatar';
import { ExternalRecipient } from './invite-people-modal';
import { InvitePermissionToggle } from './invite-permission-toggle';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// 20.4.1 section 5: three of the four verdicts refuse, each saying why. `ELIGIBLE` has no entry,
// which is what lets the field accept the address.
const BLOCKED: Partial<Record<DataRoomRecipientEligibility, StringKey>> = {
  [DataRoomRecipientEligibility.GUEST_WITH_ROOMS]: 'EMAIL_BELONGS_TO_GUEST_WITH_ROOMS',
  [DataRoomRecipientEligibility.GUEST_WITHOUT_ROOMS]: 'EMAIL_BELONGS_TO_GUEST_WITHOUT_ROOMS',
  [DataRoomRecipientEligibility.STAKEHOLDER]: 'EMAIL_BELONGS_TO_STAKEHOLDER',
};

// "Contributor" / "Auditor", as the sentence reads it.
const GUEST_ROLE_LABEL: Record<AccessRole, string> = {
  [AccessRole.AUDITOR]: 'Auditor',
  [AccessRole.CONTRIBUTOR]: 'Contributor',
};

export type InviteExternalTabProps = {
  dataRoomId: string;
  onAdd: (recipient: ExternalRecipient) => void;
  onPermissionChange: (email: string, permission: DataRoomAccessPermission) => void;
  onRemove: (email: string) => void;
  recipients: ExternalRecipient[];
  // Every email already claimed by either tab: the backend rejects a duplicate in one request,
  // so the client never lets one be assembled.
  takenEmails: Set<string>;
};

export const InviteExternalTab: FC<InviteExternalTabProps> = ({
  dataRoomId,
  onAdd,
  onPermissionChange,
  onRemove,
  recipients,
  takenEmails,
}) => {
  const [email, setEmail] = useState('');
  const [lookupEmail, setLookupEmail] = useState('');
  const [isHintDismissed, setIsHintDismissed] = useState(false);
  // Set by an attempt to add an address the field will not take, so the reason appears on the
  // first press rather than waiting for the typing to settle.
  const [wasAddAttempted, setWasAddAttempted] = useState(false);
  const { messagesLocale } = useLocale();

  const { debounceCallback } = useDebounce<string>({ callback: setLookupEmail });

  const trimmed = email.trim().toLowerCase();
  const isValid = EMAIL_PATTERN.test(trimmed);
  const isTaken = takenEmails.has(trimmed);

  // 20.4.1 section 5. The server classifies the address because three of the four answers are
  // about people the candidate list hides — a Guest Contributor or Auditor never appears there.
  const { eligibility } = useRecipientEligibility(dataRoomId, lookupEmail.trim().toLowerCase(), {
    enabled: EMAIL_PATTERN.test(lookupEmail.trim().toLowerCase()) && !isTaken,
  });

  // Only trust the answer once it is about the address currently typed: the lookup is debounced,
  // so between keystrokes it still describes the previous one.
  const hasSettled = lookupEmail.trim().toLowerCase() === trimmed;
  const verdict = hasSettled ? eligibility : undefined;

  const blockedReason = isTaken
    ? ('EMAIL_ALREADY_ADDED' as const)
    : verdict && BLOCKED[verdict.eligibility];

  /**
   * Why this address cannot be added, said while it is still on screen.
   *
   * Something that is not an address at all is refused by the field itself rather than by the
   * server, and it used to be refused in silence: Enter did nothing, the suggestion never came
   * up, and the only hint was that nothing happened. The message waits for the typing to settle,
   * or for the first attempt to add, so it does not shout at a half-typed address.
   */
  const errorMessage =
    !!trimmed && !isValid
      ? (hasSettled || wasAddAttempted) && ('ENTER_VALID_EMAIL' as const)
      : isValid && blockedReason;
  const hasError = !!errorMessage;

  const add = () => {
    if (!isValid) {
      setWasAddAttempted(true);
      return;
    }

    if (blockedReason || !verdict) return;

    onAdd({ email: trimmed, permission: DataRoomAccessPermission.VIEWER });
    setEmail('');
    setLookupEmail('');
    setWasAddAttempted(false);
  };

  return (
    <div className="flex flex-col gap-6">
      {!isHintDismissed && (
        <div className="flex gap-3 rounded-md bg-sunbeam-50 p-3">
          <InfoCircle className="size-5 shrink-0" iconColor="#DC6803" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <p className="text-sm font-[550] leading-5 text-gray-800">
              <AppFormattedMessage id="HOW_ACCESS_WORKS" />
            </p>
            <p className="text-xs font-[450] leading-4 text-gray-700">
              <AppFormattedMessage id="HOW_ACCESS_WORKS_DESCRIPTION" />
            </p>
          </div>
          <button
            aria-label={messagesLocale['CLOSE']}
            className="flex size-4 shrink-0 items-center justify-center"
            onClick={() => setIsHintDismissed(true)}
            type="button"
          >
            <XCloseIcon className="size-4" iconColor="#475467" />
          </button>
        </div>
      )}

      <div className="flex flex-col gap-4">
        <span className="text-[10px] font-semibold uppercase leading-4 tracking-[1px] text-gray-500">
          <AppFormattedMessage id="ADD_BY_EMAIL" />
        </span>

        <div className="flex flex-col gap-3">
          {/* 56 of field over a one-pixel rule, and the suggestion floats clear of the content
              below rather than pushing it down. */}
          <div className="relative">
            <input
              aria-invalid={hasError || undefined}
              className="h-14 w-full rounded-t bg-gray-50 px-2.5 text-sm font-[450] leading-5 text-gray-700 outline-none placeholder:text-gray-400"
              onChange={(event) => {
                setEmail(event.target.value);
                debounceCallback(event.target.value);
                setWasAddAttempted(false);
              }}
              onKeyDown={(event) => event.key === 'Enter' && add()}
              placeholder={messagesLocale['EMAIL']}
              value={email}
            />
            <span
              className={twMerge(
                'absolute inset-x-0 bottom-0 h-px',
                hasError ? 'bg-fireside-600' : 'bg-gray-700',
              )}
            />

            {isValid && !blockedReason && (
              <div className="absolute inset-x-0 top-full z-10 mt-1 rounded bg-white p-2 shadow-[0px_-4px_16px_0px_rgba(16,24,40,0.04),0px_24px_48px_-12px_rgba(16,24,40,0.18)]">
                <button
                  className="flex h-9 w-full items-center justify-between rounded bg-brand-25 px-2 text-left"
                  onClick={add}
                  type="button"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm leading-5 text-gray-700">{trimmed}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="text-sm font-medium leading-4 text-brand-700">
                      <AppFormattedMessage id="ADD_RECIPIENT" />
                    </span>
                    <ChevronDownIcon className="size-4 -rotate-90" />
                  </span>
                </button>
              </div>
            )}
          </div>

          {errorMessage && (
            <p className="flex items-center gap-1 text-xs font-[450] leading-4 text-fireside-600">
              <InfoCircle className="size-3 shrink-0" iconColor="#D92D20" />
              <AppFormattedMessage
                id={errorMessage}
                values={{ role: verdict?.guestRole ? GUEST_ROLE_LABEL[verdict.guestRole] : '' }}
              />
            </p>
          )}
        </div>
      </div>

      {recipients.length > 0 && (
        <div className="w-full overflow-hidden rounded-md border border-gray-100">
          <div className="flex h-11 items-center bg-gray-50 px-2">
            <span className="text-label-md font-medium text-[#172335]">
              <AppFormattedMessage id="STAKEHOLDERS_COUNT" values={{ count: recipients.length }} />
            </span>
          </div>

          {recipients.map((recipient) => (
            <div
              className="flex h-[72px] items-center justify-between gap-4 border-t border-gray-100 px-2"
              key={recipient.email}
            >
              <div className="flex min-w-0 items-center gap-3">
                <AccessAvatar
                  label={recipient.email}
                  labelClassName="text-[12.8px] leading-[19.2px]"
                />
                <p className="truncate text-sm font-[450] leading-5 text-gray-700">
                  {recipient.email}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-4">
                {/* Always external here: an address that belongs to a stakeholder is refused at
                    the field above, so nothing on this list can be anything else. */}
                <Tag
                  borderColor="border-transparent"
                  className="bg-[#FCFAFF] text-[#7F56D9]"
                  title={<AppFormattedMessage id="EXTERNAL" />}
                  variant={TagVariant.WHITE}
                />
                <InvitePermissionToggle
                  onChange={(permission) => onPermissionChange(recipient.email, permission)}
                  value={recipient.permission}
                />
                <button aria-label="remove" onClick={() => onRemove(recipient.email)} type="button">
                  <XCloseIcon className="size-4" iconColor="#344054" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
